import { cn } from '@aw/lib'
import { cva, VariantProps } from 'class-variance-authority'
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { motion } from 'motion/react'
import { XMarkIcon } from "@aw/icons/outline"
import { TriangleExclamation } from "@aw/icons/outline"
import "./style.css"
// Variants
export const InputVariants = cva(
    "w-full focus:outline-none text-foreground font-medium disabled:cursor-not-allowed",
    {
        variants: {
            size: {
                xs: "h-[calc(2.25rem-2px)] rounded-lg text-sm font-medium",
                sm: "h-[calc(3rem-2px)] text-base rounded-[0.63rem] font-medium",
                md: "h-[calc(3.5rem-2px)] text-base rounded-xl font-medium",
                icon: "size-9 hover:bg-neutral-3 dark:bg-inherit rounded-full",
            },
        },
        defaultVariants: {
            size: "sm",
        },
    }
)

export const OutlineVariants = cva(
    "bg-canvas px-3 justify-stretch w-full border  flex items-center gap-[0.625rem]",
    {
        variants: {
            size: {
                default: "rounded-[0.63rem] min-w-[4.25rem]",
                xs: "rounded-lg min-w-[2.685rem]",
                sm: "rounded-[0.63rem] min-w-[4.25rem]",
                md: "rounded-xl min-w-16",
                icon: "size-9 hover:bg-neutral-3 dark:bg-inherit rounded-full",
            },
        },
        defaultVariants: {
            size: "sm",
        }
    }
)


type IconProps = {
    className?: string
    color?: string
} & React.SVGProps<SVGSVGElement>

interface InputProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "color">,
    VariantProps<typeof InputVariants> {
    hasError?: boolean
    loading?: boolean
    ref?: React.Ref<HTMLInputElement>
    disabled?: boolean
    onChange: (e: ChangeEvent<HTMLInputElement>) => void
    onErrorClear?: () => void
    LeftIcon?: React.ComponentType<IconProps>
    RightIcon?: React.ComponentType<IconProps>
    errorMessage?: string
    hint?: string
    allowClear?: boolean
    onClear?: () => void
    testId?: string
}

function Input({
    RightIcon,
    LeftIcon,
    value,
    onChange,
    onErrorClear,
    className,
    hasError,
    errorMessage,
    onFocus,
    onBlur,
    hint,
    ref,
    onClear,
    size = "sm",
    testId,
    allowClear = true,
    ...props
}: InputProps) {

    const [isFocused, setIsFocused] = useState(false)
    const internalRef = useRef<HTMLInputElement | null>(null)

    // Detect autofill from password managers (LastPass, 1Password, etc.)
    // They set .value directly on the DOM without triggering React's onChange
    useEffect(() => {
        const el = internalRef.current
        if (!el) return

        const handleAutoFill = () => {
            if (el.value !== value) {
                const syntheticEvent = {
                    target: el,
                    currentTarget: el,
                } as ChangeEvent<HTMLInputElement>
                onChange(syntheticEvent)
            }
        }

        el.addEventListener('input', handleAutoFill)
        // Some managers trigger 'change' instead of 'input'
        el.addEventListener('change', handleAutoFill)

        return () => {
            el.removeEventListener('input', handleAutoFill)
            el.removeEventListener('change', handleAutoFill)
        }
    }, [value, onChange])

    const setRefs = useCallback((node: HTMLInputElement | null) => {
        internalRef.current = node
        if (typeof ref === 'function') {
            ref(node)
        } else if (ref) {
            (ref as React.RefObject<HTMLInputElement | null>).current = node
        }
    }, [ref])

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (hasError && onErrorClear) {
            onErrorClear()
        }
        onChange(e)
    }

    const handleClear = () => {
        if (hasError && onErrorClear) {
            onErrorClear()
        }
        if (onChange) {
            const syntheticEvent = {
                target: { value: '', name: props.name },
                currentTarget: { value: '', name: props.name }
            } as ChangeEvent<HTMLInputElement>
            onChange(syntheticEvent)
        }
        if (onClear) {
            onClear()
        }
    }

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true)
        if (onFocus) {
            onFocus(e)
        }
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false)
        if (onBlur) {
            onBlur(e)
        }
    }


    const getBorderColor = () => {
        if (hasError) return "var(--status-danger)"
        if (isFocused) return "var(--input-border-focus)"
        return "var(--input-border)"
    }

    return (
        <div className='w-full'>
            <motion.div
                className={cn(
                    OutlineVariants({ size }),
                    className,
                    hasError ? "border-red-base" :
                        isFocused ? "border-muted-foreground" : "border-gunmetal-300",
                    props.disabled ? "opacity-50 cursor-not-allowed" : ""
                )}
                animate={{
                    borderColor: getBorderColor()
                }}
                transition={{
                    duration: 0.2,
                    ease: "easeInOut"
                }}
            >
                {LeftIcon &&
                    <div
                        className='cursor-pointer  rounded-sm size-4 flex items-center justify-center'
                    >
                        <LeftIcon className='size-4 fill-placeholder' />
                    </div>
                }
                <div className="flex-1">
                    <input
                        data-testid={testId}
                        data-lpignore="false"
                        ref={setRefs}
                        className={cn(InputVariants({ size }))}
                        value={value}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder={props?.placeholder}
                        {...props}
                    />
                </div>

                {hasError ? (
                    <motion.div
                        className='cursor-pointer rounded-sm size-4 flex items-center justify-center'
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{
                            duration: 0.2,
                            ease: "easeOut"
                        }}
                    >
                        <TriangleExclamation className='size-4 fill-red-base' />
                    </motion.div>
                ) : RightIcon ? (
                    <button type="button"
                        data-testid={testId ? `${testId}-suffix-btn` : undefined}
                        className='cursor-pointer hover:bg-hover rounded-sm size-4 flex items-center justify-center'
                    >
                        <RightIcon className="size-4" />
                    </button>
                ) : (value && !props?.disabled && allowClear) ? (
                    <button
                        data-testid={testId ? `${testId}-clear-btn` : undefined}
                        tabIndex={-1}
                        type="button"
                        className='cursor-pointer hover:bg-hover rounded-sm size-4 flex items-center justify-center z-[10]'
                        onClick={handleClear}
                    >
                        <XMarkIcon className='size-[0.875rem] fill-placeholder' />
                    </button>
                ) : null}
            </motion.div>

            {hint && !hasError && (
                <div className='text-muted-foreground text-sm ml-3'>
                    {hint}
                </div>
            )}

            {hasError && (
                <motion.div
                    data-testid={testId ? `${testId}-error` : undefined}
                    className='text-red-base text-sm ml-3'
                    initial={{
                        opacity: 0,
                        y: -8,
                        height: 0
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                        height: "auto"
                    }}
                    exit={{
                        opacity: 0,
                        y: -8,
                        height: 0
                    }}
                    transition={{
                        duration: 0.3,
                        ease: "easeInOut"
                    }}
                >
                    {errorMessage}
                </motion.div>
            )}
        </div>
    )
}

export default Input